import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Grid,
  CircularProgress,
  IconButton,
  Chip,
  Divider,
  Card,
  CardContent,
  CardActions,
  Tooltip,
  Alert,
  Snackbar,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import logo from "./assets/image/LOGO_FCN.png";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import GroupsIcon from "@mui/icons-material/Groups";
import RefreshIcon from "@mui/icons-material/Refresh";
import { jsPDF } from "jspdf";

// ===== IMPORTAR SERVICIO API CENTRALIZADO =====
import { apiService, handleApiError } from '../config/api';

// Paleta de colores renovada - más sobria y ejecutiva (mantenida igual)
const theme = {
  primary: {
    main: "#93684b", 
    light: "#5e4a46",
    dark: "#462918", 
    contrastText: "#FFFFFF",
  },
  secondary: {
    main: "#BF995C", // Dorado corporativo
    light: "#D6B77F", // Dorado claro
    dark: "#9A7842", // Dorado oscuro
    contrastText: "#FFFFFF",
  },
  background: {
    default: "#F5F5F5", // Gris muy claro
    paper: "#FFFFFF", // Blanco
  },
  success: {
    main: "#2E7D32", // Verde más sobrio
  },
  warning: {
    main: "#de1d0a", // Naranja más sobrio
  },
  error: {
    main: "#D32F2F", // Rojo más sobrio
  },
};

// Componentes Estilizados (mantenidos iguales)
const Container = styled(Box)(() => ({
  padding: "2rem",
  backgroundColor: "#ffffff",
  borderRadius: "8px",
  boxShadow: "0 3px 10px rgba(0, 0, 0, 0.08)",
  maxWidth: "100%",
  width: "100%",
  margin: "0 auto",
  overflowX: "auto",
}));

const StyledButton = styled(Button)(() => ({
  backgroundColor: theme.primary.main,
  color: theme.primary.contrastText,
  fontWeight: 600,
  padding: "0.5rem 1.5rem",
  borderRadius: "4px",
  textTransform: "none",
  transition: "all 0.2s ease",
  boxShadow: "0 2px 5px rgba(0, 0, 0, 0.08)",
  "&:hover": {
    backgroundColor: theme.primary.dark,
    boxShadow: "0 3px 8px rgba(0, 0, 0, 0.12)",
  },
}));

const ExportButton = styled(Button)(() => ({
  backgroundColor: theme.secondary.main,
  color: theme.secondary.contrastText,
  fontWeight: 600,
  padding: "0.6rem 2rem",
  borderRadius: "4px",
  textTransform: "none",
  transition: "all 0.2s ease",
  boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
  "&:hover": {
    backgroundColor: theme.secondary.dark,
    boxShadow: "0 3px 8px rgba(0, 0, 0, 0.15)",
  },
}));

const AsociacionCard = styled(Card)(() => ({
  borderRadius: "4px",
  overflow: "hidden",
  transition: "box-shadow 0.2s ease",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  "&:hover": {
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
  },
}));

const AsociacionHeader = styled(Box)(() => ({
  backgroundColor: theme.primary.main,
  padding: "1rem",
  position: "relative",
  overflow: "hidden",
}));

const TableStyled = styled(Table)(() => ({
  width: "100%",
  borderCollapse: "separate",
  borderSpacing: 0,
  "& th:first-of-type": {
    borderTopLeftRadius: "8px",
  },
  "& th:last-of-type": {
    borderTopRightRadius: "8px",
  },
}));

const TableHeadStyled = styled(TableHead)(() => ({
  backgroundColor: alpha(theme.primary.light, 0.8),
}));

const TableCellStyled = styled(TableCell)(() => ({
  padding: "0.75rem 1rem",
  border: "1px solid #e0e0e0",
  textAlign: "center",
  color: theme.primary.dark,
  fontSize: "0.875rem",
}));

const TableHeaderCellStyled = styled(TableCell)(() => ({
  padding: "0.75rem 1rem",
  border: "1px solid #e0e0e0",
  textAlign: "center",
  color: "#fff",
  fontWeight: 600,
  fontSize: "0.9rem",
  backgroundColor: theme.primary.main,
}));

const TableRowStyled = styled(TableRow)(() => ({
  "&:nth-of-type(even)": {
    backgroundColor: alpha(theme.primary.light, 0.05),
  },
  "&:hover": {
    backgroundColor: alpha(theme.primary.light, 0.1),
    transition: "background-color 0.2s ease",
  },
}));

const StatCard = styled(Box)(() => ({
  padding: "1rem",
  backgroundColor: "#fff",
  borderRadius: "8px",
  boxShadow: "0 3px 10px rgba(0, 0, 0, 0.08)",
  marginBottom: "1rem",
  transition: "transform 0.2s ease",
  "&:hover": {
    transform: "translateY(-2px)",
  },
}));

const StatsContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  gap: "0.5rem",
  backgroundColor: alpha(theme.background.default, 0.5),
  borderRadius: "8px",
  padding: "1rem",
  marginTop: "1rem",
  border: `1px solid ${alpha(theme.primary.light, 0.2)}`,
}));

// Componente para el resumen de una asociación (mantenido igual)
const AsociacionResumen = ({ item, onOpenModal }) => {
  const camposFaltantesPorRepresentante = item.camposPorRepresentante.reduce(
    (acc, curr) => {
      Object.keys(curr.camposVacios).forEach((campo) => {
        acc[campo] = (acc[campo] || 0) + 1;
      });
      return acc;
    },
    {}
  );

  const porcentajeCompletitud = Math.round(
    (item.representantesConCamposLlenos / item.representantes) * 100
  );

  const getStatusColor = () => {
    if (porcentajeCompletitud >= 90) return theme.success.main;
    if (porcentajeCompletitud >= 60) return theme.warning.main;
    return theme.error.main;
  };

  return (
    <AsociacionCard>
      <AsociacionHeader>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: "#fff",
            textAlign: "center",
            position: "relative",
            zIndex: 1,
          }}
        >
          {item.asociacion}
        </Typography>
      </AsociacionHeader>

      <CardContent sx={{ flexGrow: 1, p: 2 }}>
        <Box 
          sx={{ 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "space-between", 
            mb: 2,
            px: 1
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <GroupsIcon fontSize="small" />
            Total: {item.representantes}
          </Typography>
          
          <Box 
            sx={{ 
              borderRadius: '20px', 
              px: 1.5, 
              py: 0.5, 
              bgcolor: alpha(getStatusColor(), 0.1), 
              color: getStatusColor(),
              fontWeight: 600,
              fontSize: '0.75rem',
              border: `1px solid ${alpha(getStatusColor(), 0.3)}`
            }}
          >
            {porcentajeCompletitud}% Completado
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography 
          variant="subtitle1" 
          sx={{ 
            fontWeight: 600, 
            color: theme.primary.dark,
            mb: 1.5,
            fontSize: '0.9rem'
          }}
        >
          Datos pendientes de registro
        </Typography>

        {Object.entries(camposFaltantesPorRepresentante).length === 0 ? (
          <Box 
            sx={{ 
              p: 2, 
              textAlign: "center", 
              backgroundColor: alpha(theme.success.main, 0.05),
              borderRadius: "4px",
              border: `1px solid ${alpha(theme.success.main, 0.2)}`,
            }}
          >
            <Typography variant="body2" sx={{ color: theme.success.main, fontWeight: 500 }}>
              Todos los datos están completos
            </Typography>
          </Box>
        ) : (
          <TableStyled>
            <TableHeadStyled>
              <TableRowStyled>
                <TableHeaderCellStyled>Campo</TableHeaderCellStyled>
                <TableHeaderCellStyled>Pendientes</TableHeaderCellStyled>
              </TableRowStyled>
            </TableHeadStyled>
            <TableBody>
              {Object.entries(camposFaltantesPorRepresentante).map(
                ([campo, count], idx) => (
                  <TableRowStyled key={idx}>
                    <TableCellStyled sx={{ textAlign: 'left', fontWeight: 500 }}>{campo}</TableCellStyled>
                    <TableCellStyled sx={{ fontWeight: 500 }}>
                      {count}
                    </TableCellStyled>
                  </TableRowStyled>
                )
              )}
            </TableBody>
          </TableStyled>
        )}

        <StatsContainer>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CheckCircleIcon sx={{ color: theme.success.main, fontSize: 16 }} />
                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                  <strong>Completos:</strong> {item.representantesConCamposLlenos}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={6}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <ErrorIcon sx={{ color: theme.error.main, fontSize: 16 }} />
                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                  <strong>Pendientes:</strong> {item.representantesConCamposVacios}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </StatsContainer>
      </CardContent>

      <CardActions sx={{ justifyContent: "center", pb: 2 }}>
        <StyledButton
          onClick={() => onOpenModal(item.asociacion)}
          startIcon={<ExpandMoreIcon />}
          size="medium"
          fullWidth
          sx={{ mx: 2 }}
        >
          Ver Detalles
        </StyledButton>
      </CardActions>
    </AsociacionCard>
  );
};

// Componente para el modal de detalles (mantenido igual)
const DetallesModal = ({ open, onClose, asociacion, reporte }) => {
  const asociacionData = reporte.find((item) => item.asociacion === asociacion);

  if (!asociacionData) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth 
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: "8px",
          overflow: "hidden",
          boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          backgroundColor: theme.primary.main, 
          color: "#fff",
          p: 2,
          fontWeight: 600,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid rgba(0,0,0,0.1)"
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <GroupsIcon />
          Detalles de la Asociación: {asociacion}
        </Box>
        <IconButton
          aria-label="cerrar"
          onClick={onClose}
          sx={{
            color: "#fff",
            backgroundColor: alpha("#fff", 0.1),
            "&:hover": {
              backgroundColor: alpha("#fff", 0.2),
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers sx={{ p: 3 }}>
        <Paper elevation={0} sx={{ p: 2, mb: 3, backgroundColor: alpha(theme.background.default, 0.5), border: "1px solid #e0e0e0" }}>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Box sx={{ textAlign: "center", p: 1, borderRight: "1px solid #e0e0e0" }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: theme.primary.main }}>
                  {asociacionData.representantes}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Total Representantes
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box sx={{ textAlign: "center", p: 1, borderRight: "1px solid #e0e0e0" }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: theme.success.main }}>
                  {asociacionData.representantesConCamposLlenos}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Completos
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box sx={{ textAlign: "center", p: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: theme.error.main }}>
                  {asociacionData.representantesConCamposVacios}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Pendientes
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
        
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: theme.primary.dark }}>
          Estado de datos por representante
        </Typography>
        
        <TableStyled sx={{ boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}>
          <TableHeadStyled>
            <TableRowStyled>
              <TableHeaderCellStyled sx={{ width: "40%" }}>Representante</TableHeaderCellStyled>
              <TableHeaderCellStyled sx={{ width: "30%" }}>Estado</TableHeaderCellStyled>
              <TableHeaderCellStyled sx={{ width: "30%" }}>Campos Pendientes</TableHeaderCellStyled>
            </TableRowStyled>
          </TableHeadStyled>
          <TableBody>
            {asociacionData.camposPorRepresentante.map((representante, idx) => {
              const camposFaltantes = Object.keys(representante.camposVacios);
              const totalCamposFaltantes = camposFaltantes.length;
              const estCompleto = totalCamposFaltantes === 0;
              
              return (
                <TableRowStyled key={idx}>
                  <TableCellStyled sx={{ fontWeight: 500, textAlign: "left" }}>
                    {representante.nombre || "Nombre no disponible"}
                  </TableCellStyled>
                  <TableCellStyled>
                    {estCompleto ? (
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                        <CheckCircleIcon sx={{ color: theme.success.main, fontSize: 18 }} />
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: theme.success.main, 
                            fontWeight: 500,
                          }}
                        >
                          Completo
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                        <ErrorIcon sx={{ color: theme.error.main, fontSize: 18 }} />
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: theme.error.main, 
                            fontWeight: 500,
                          }}
                        >
                          {totalCamposFaltantes} {totalCamposFaltantes === 1 ? 'campo pendiente' : 'campos pendientes'}
                        </Typography>
                      </Box>
                    )}
                  </TableCellStyled>
                  <TableCellStyled>
                    {estCompleto ? (
                      <Typography variant="body2" sx={{ color: theme.success.main, fontStyle: "italic" }}>
                        -
                      </Typography>
                    ) : (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, justifyContent: "center" }}>
                        {camposFaltantes.map((campo, campoIdx) => (
                          <Chip 
                            key={campoIdx} 
                            label={campo} 
                            size="small"
                            sx={{ 
                              backgroundColor: "#f5f5f5",
                              color: theme.primary.dark,
                              fontWeight: 500,
                              border: `1px solid ${alpha(theme.primary.main, 0.2)}`,
                              mb: 0.5,
                              fontSize: "0.7rem"
                            }} 
                          />
                        ))}
                      </Box>
                    )}
                  </TableCellStyled>
                </TableRowStyled>
              );
            })}
          </TableBody>
        </TableStyled>
      </DialogContent>
      
      <DialogActions sx={{ p: 2, backgroundColor: alpha(theme.background.default, 0.5) }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            color: theme.primary.main,
            borderColor: theme.primary.main,
            "&:hover": {
              backgroundColor: alpha(theme.primary.main, 0.08),
            },
          }}
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Componente principal
const Sumatoria = () => {
  // ===== ESTADOS =====
  const [representantes, setRepresentantes] = useState([]);
  const [asociaciones, setAsociaciones] = useState([]);
  const [reporte, setReporte] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [selectedAsociacion, setSelectedAsociacion] = useState(null);
  
  // Estados para notificaciones
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // ===== FUNCIONES DE LÓGICA DE NEGOCIO (mantenidas iguales) =====
  
  // Función para verificar si un campo está vacío
  const estaVacio = (campo) => {
    return campo === "" || campo === null || campo === undefined;
  };

  // Función para generar reporte (mantenida igual)
  const generarReporte = (representantes, asociaciones) => {
    const camposExcluidos = [
      "Asignación",
      "Organización",
      "Observaciones",
      "Cooperativa",
      "Número",
      "Tomo",
      "Libro",
      "Fecha Constancia",
      "CONSUCOOP",
      "cod_cooperativa",
      "organizacion",
    ];
    
    let resultado = [];
    asociaciones.forEach((asociacion) => {
      const asociacionRepresentantes = representantes.filter(
        (representante) =>
          representante.cod_asociacion === asociacion.cod_asociacion
      );
      
      let totalCamposVaciosAsociacion = 0;
      let representantesConCamposVacios = 0;
      let representantesConCamposLlenos = 0;
      
      let camposParaConsiderar = [
        "reporte_cosecha",
        "organizacion",
        "telefono",
        "cod_cooperativa",
        "domicilio",
        "numero",
        "tomo",
        "libro",
        "fecha_constancia",
      ];
      
      if (asociacion.cod_asociacion !== 6) {
        camposParaConsiderar = camposParaConsiderar.filter(
          (campo) =>
            !["numero", "tomo", "libro", "fecha_constancia"].includes(campo)
        );
      }
      
      let camposPorRepresentante = asociacionRepresentantes.map(
        (representante) => {
          const camposVaciosPorRepresentante = {};
          let camposVacios = 0;
          
          camposParaConsiderar.forEach((campo) => {
            if (!camposExcluidos.includes(campo)) {
              const estaVacioCampo = estaVacio(representante[campo]);
              if (estaVacioCampo) {
                totalCamposVaciosAsociacion++;
                camposVacios++;
                camposVaciosPorRepresentante[campo] = 1;
              }
            }
          });
          
          if (camposVacios === 0) {
            representantesConCamposLlenos++;
          } else {
            representantesConCamposVacios++;
          }
          
          return {
            nombre: representante.nombre_representante || "Sin nombre",
            camposVacios: camposVaciosPorRepresentante,
          };
        }
      );
      
      resultado.push({
        asociacion: asociacion.nombre_abreviado,
        representantes: asociacionRepresentantes.length,
        camposVacios: totalCamposVaciosAsociacion,
        representantesConCamposVacios,
        representantesConCamposLlenos,
        camposPorRepresentante,
        nombresRepresentantes: asociacionRepresentantes.map(
          (rep) => rep.nombre_representante || "Sin nombre"
        ),
      });
    });
    
    return resultado;
  };

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      
      const datosReporte = await apiService.utils.getDatosReporte();
      
      const {
        representantes: representantesData,
        asociaciones: asociacionesData
      } = datosReporte;
      setRepresentantes(representantesData);
      setAsociaciones(asociacionesData);
      
      // Generar reporte con los datos obtenidos
      const resultado = generarReporte(representantesData, asociacionesData);
      setReporte(resultado);
      
      // Mostrar mensaje de éxito
      setSnackbarMessage(`Datos cargados exitosamente: ${representantesData.length} representantes de ${asociacionesData.length} asociaciones`);
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
      
    } catch (error) {
      
      // Usar el manejador de errores centralizado
      const errorMessage = handleApiError(error, "Error al cargar los datos del reporte");
      setError(errorMessage);
      
      // Mostrar mensaje de error
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      
      // En caso de error, establecer arrays vacíos para evitar crashes
      setRepresentantes([]);
      setAsociaciones([]);
      setReporte([]);
      
    } finally {
      setIsLoading(false);
    }
  };

  // ===== FUNCIÓN DE GENERACIÓN DE PDF (mejorada con manejo de errores) =====
  const generarPDF = () => {
    try {
      if (!reporte || reporte.length === 0) {
        setSnackbarMessage("No hay datos disponibles para generar el PDF");
        setSnackbarSeverity("warning");
        setOpenSnackbar(true);
        return;
      }

      const doc = new jsPDF();
      const margin = 10;
      let yPosition = margin;
      const lineHeight = 8;
      const maxHeight = doc.internal.pageSize.height - margin * 2;
      const pageWidth = doc.internal.pageSize.width;

      // Fondo con degradado
      doc.setFillColor(255, 255, 255);
      doc.rect(0, 0, pageWidth, doc.internal.pageSize.height, "F");
      
      doc.setFillColor(250, 250, 250);
      for (let i = 0; i < doc.internal.pageSize.height; i += 20) {
        doc.setFillColor(248, 248, 248);
        doc.rect(0, i, pageWidth, 10, "F");
      }

      // Encabezado con logo
      try {
        const imgWidth = 30;
        const imgHeight = 30;
        doc.addImage(logo, "PNG", margin, margin, imgWidth, imgHeight);
      } catch (logoError) {
      }

      yPosition += 20;

      // Título y subtítulo con estilo mejorado
      doc.setFontSize(24);
      doc.setFont("times", "bold");
      doc.setTextColor(93, 64, 55);
      const title = "FONDO CAFETERO NACIONAL";
      const titleX = (pageWidth - doc.getTextWidth(title)) / 2;
      doc.text(title, titleX, yPosition);
      
      yPosition += 2;
      doc.setDrawColor(239, 204, 2);
      doc.setLineWidth(1);
      doc.line(titleX, yPosition, titleX + doc.getTextWidth(title), yPosition);
      
      yPosition += 13;

      doc.setFontSize(18);
      const subtitle = "RESUMEN GREMIALES";
      const subtitleX = (pageWidth - doc.getTextWidth(subtitle)) / 2;
      doc.text(subtitle, subtitleX, yPosition);
      yPosition += 20;

      let pageIndex = 1;
      let asociacionesEnPaginaActual = 0;

      reporte.forEach((item) => {
        if (yPosition > maxHeight || asociacionesEnPaginaActual === 2) {
          doc.addPage();
          yPosition = margin;
          asociacionesEnPaginaActual = 0;
          pageIndex++;
          
          // Repetir la textura de fondo en cada página
          doc.setFillColor(255, 255, 255);
          doc.rect(0, 0, pageWidth, doc.internal.pageSize.height, "F");
          for (let i = 0; i < doc.internal.pageSize.height; i += 20) {
            doc.setFillColor(248, 248, 248);
            doc.rect(0, i, pageWidth, 10, "F");
          }
        }

        // Fondo para el título de la asociación
        doc.setFillColor(93, 64, 55);
        doc.rect(margin - 2, yPosition - 7, pageWidth - (margin * 2) + 4, 15, "F");
        
        // Título de la asociación
        const asociacionText = `Asociación: ${item.asociacion}`;
        doc.setFontSize(14);
        doc.setFont("times", "bold");
        doc.setTextColor(255, 255, 255);
        doc.text(asociacionText, margin, yPosition);
        yPosition += 12;

        doc.setFontSize(12);
        doc.setFont("times", "normal");
        doc.setTextColor(61, 61, 61);
        yPosition += lineHeight;

        // Resumen de campos faltantes con estilo mejorado
        const camposFaltantes = Object.entries(
          item.camposPorRepresentante.reduce((acc, curr) => {
            Object.keys(curr.camposVacios).forEach((campo) => {
              acc[campo] = (acc[campo] || 0) + 1;
            });
            return acc;
          }, {})
        );

        if (camposFaltantes.length === 0) {
          // Mensaje estilizado para "no hay datos"
          doc.setFillColor(236, 252, 237);
          doc.rect(margin, yPosition - 5, pageWidth - (margin * 2), 12, "F");
          doc.setTextColor(76, 175, 80);
          doc.setFont("times", "bold");
          doc.text("No hay datos pendientes para mostrar", margin + 5, yPosition + 2);
          yPosition += lineHeight + 4;
        } else {
          const colWidth = (pageWidth - margin * 2) / 2;

          // Encabezados de tabla con estilo mejorado
          doc.setFontSize(10);
          doc.setFont("times", "bold");
          doc.setTextColor(255, 255, 255);
          doc.setFillColor(93, 64, 55);
          doc.rect(margin, yPosition - 5, colWidth, 8, "F");
          doc.rect(margin + colWidth, yPosition - 5, colWidth, 8, "F");
          doc.text("Datos faltantes", margin + 5, yPosition + 2);
          doc.text(
            "Cantidad de representantes",
            margin + colWidth + 5,
            yPosition + 2
          );
          yPosition += 10;

          // Filas de la tabla con estilo alternado
          camposFaltantes.forEach(([campo, count], index) => {
            if (yPosition + 8 > maxHeight) {
              doc.addPage();
              yPosition = margin;
              
              // Repetir la textura de fondo
              doc.setFillColor(255, 255, 255);
              doc.rect(0, 0, pageWidth, doc.internal.pageSize.height, "F");
              for (let i = 0; i < doc.internal.pageSize.height; i += 20) {
                doc.setFillColor(248, 248, 248);
                doc.rect(0, i, pageWidth, 10, "F");
              }
            }

            // Alternar colores de fondo para las filas
            if (index % 2 === 0) {
              doc.setFillColor(245, 245, 245);
              doc.rect(margin, yPosition - 5, colWidth * 2, 8, "F");
            }

            const campoX = margin + colWidth / 2 - doc.getTextWidth(campo) / 2;
            const countX =
              margin +
              colWidth +
              colWidth / 2 -
              doc.getTextWidth(`${count} Representantes`) / 2;

            // Dibujar las celdas
            doc.setDrawColor(220, 220, 220);
            doc.rect(margin, yPosition - 5, colWidth, 8);
            doc.rect(margin + colWidth, yPosition - 5, colWidth, 8);

            // Centrar el texto dentro de cada celda
            doc.setTextColor(61, 61, 61);
            doc.setFont("times", "normal");
            doc.text(campo, campoX, yPosition);
            
            // Destacar el número de representantes
            doc.setFont("times", "bold");
            doc.text(`${count} Representantes`, countX, yPosition);

            yPosition += lineHeight;
          });
        }
        
        yPosition += 8;
        
        // Título de Resumen con decoración
        const resumenText = "Resumen de Representantes";
        doc.setFontSize(14);
        doc.setFont("times", "bold");
        doc.setTextColor(93, 64, 55);
        const resumenX = (pageWidth - doc.getTextWidth(resumenText)) / 2;
        
        // Línea decorativa antes del título
        doc.setDrawColor(239, 204, 2);
        doc.line(margin, yPosition - 2, resumenX - 5, yPosition - 2);
        
        doc.text(resumenText, resumenX, yPosition);
        
        // Línea decorativa después del título
        doc.line(resumenX + doc.getTextWidth(resumenText) + 5, yPosition - 2, pageWidth - margin, yPosition - 2);
        
        yPosition += 10;

        // Recuadro para estadísticas con fondo suave
        doc.setFillColor(248, 248, 248);
        doc.setDrawColor(220, 220, 220);
        doc.roundedRect(margin, yPosition - 5, pageWidth - (margin * 2), lineHeight * 4, 3, 3, "FD");

        // Detalles de los representantes con iconos simulados
        doc.setFontSize(10);
        doc.setFont("times", "normal");
        doc.setTextColor(61, 61, 61);
        
        // Círculo verde para "Cumplen con los requisitos"
        doc.setFillColor(76, 175, 80);
        doc.circle(margin + 3, yPosition, 2, "F");
        doc.setFont("times", "bold");
        doc.text("Cumplen con los requisitos:", margin + 7, yPosition);
        doc.setFont("times", "normal");
        doc.text(`${item.representantesConCamposLlenos} Representantes`, margin + 70, yPosition);
        yPosition += lineHeight;
        
        // Círculo azul para "Representantes registrados"
        doc.setFillColor(41, 121, 255);
        doc.circle(margin + 3, yPosition, 2, "F");
        doc.setFont("times", "bold");
        doc.text("Representantes registrados:", margin + 7, yPosition);
        doc.setFont("times", "normal");
        doc.text(`${item.representantes} Representantes`, margin + 70, yPosition);
        yPosition += lineHeight;
        
        // Círculo naranja para "Subsanar requerimientos"
        doc.setFillColor(255, 152, 0);
        doc.circle(margin + 3, yPosition, 2, "F");
        doc.setFont("times", "bold");
        doc.text("Subsanar requerimientos:", margin + 7, yPosition);
        doc.setFont("times", "normal");
        doc.text(`${item.representantesConCamposVacios} Representantes`, margin + 70, yPosition);
        yPosition += lineHeight + 4;

        // Línea de separación ornamental
        doc.setLineWidth(0.5);
        doc.setDrawColor(239, 204, 2);
        doc.line(margin, yPosition, pageWidth - margin, yPosition);
        
        // Patrón decorativo en la línea
        for (let i = margin + 10; i < pageWidth - margin; i += 20) {
          doc.setFillColor(93, 64, 55);
          doc.circle(i, yPosition, 1, "F");
        }
        
        yPosition += 12;

        // Aumentamos el contador de asociaciones procesadas
        asociacionesEnPaginaActual++;
      });

      // Pie de página
      const totalPages = pageIndex;
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(`Página ${i} de ${totalPages} - Fondo Cafetero Nacional`, pageWidth / 2, doc.internal.pageSize.height - 10, { align: "center" });
      }

      // Guardar el PDF
      const currentDate = new Date();
      const dateStr = currentDate.toISOString().split('T')[0];
      doc.save(`reporte_datos_registrados_${dateStr}.pdf`);

      // Mostrar mensaje de éxito
      setSnackbarMessage("Reporte PDF generado exitosamente");
      setSnackbarSeverity("success");
      setOpenSnackbar(true);

    } catch (error) {
      setSnackbarMessage("Error al generar el reporte PDF: " + error.message);
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    }
  };

  // ===== EFECTOS =====
  useEffect(() => {
    fetchData();
  }, []);

  // ===== MANEJADORES DE EVENTOS =====
  const handleOpenModal = (asociacion) => {
    setSelectedAsociacion(asociacion);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedAsociacion(null);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handleRetry = () => {
    fetchData();
  };

  // ===== RENDERIZADO CONDICIONAL =====
  
  // Estado de carga
  if (isLoading) {
    return (
      <Container 
        sx={{ 
          display: "flex", 
          flexDirection: "column",
          justifyContent: "center", 
          alignItems: "center", 
          height: "300px",
          backgroundImage: "linear-gradient(135deg, #f5f7fa 0%, #f9fcff 100%)",
        }}
      >
        <CircularProgress sx={{ color: theme.primary.main, mb: 2 }} />
        <Typography sx={{ fontWeight: 500, color: theme.primary.main, mb: 1 }}>
          Cargando datos con API centralizada...
        </Typography>
        <Typography variant="body2" sx={{ color: theme.primary.light, textAlign: 'center' }}>
          Obteniendo información de representantes y asociaciones
        </Typography>
      </Container>
    );
  }

  // Estado de error
  if (error) {
    return (
      <Container>
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4, 
            textAlign: "center",
            borderTop: `4px solid ${theme.error.main}`,
            borderRadius: "12px",
          }}
        >
          <ErrorIcon sx={{ fontSize: 48, color: theme.error.main, mb: 2 }} />
          <Typography variant="h5" color="error" gutterBottom>
            Error al cargar los datos
          </Typography>
          <Typography color="textSecondary" paragraph>
            {error}
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
            <Button 
              variant="contained" 
              color="primary"
              onClick={handleRetry}
              startIcon={<RefreshIcon />}
              sx={{
                backgroundColor: theme.primary.main,
                "&:hover": {
                  backgroundColor: theme.primary.dark,
                },
              }}
            >
              Reintentar
            </Button>
            
            <Button 
              variant="outlined" 
              onClick={() => window.location.reload()}
              sx={{
                color: theme.primary.main,
                borderColor: theme.primary.main,
                "&:hover": {
                  backgroundColor: alpha(theme.primary.main, 0.08),
                },
              }}
            >
              Recargar página
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  // ===== RENDERIZADO PRINCIPAL =====
  return (
    <Container>
      {/* Snackbar para notificaciones */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Header con título */}
      <Box 
        sx={{ 
          textAlign: "center", 
          mb: 4,
          position: "relative",
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: "-8px",
            left: "50%",
            transform: "translateX(-50%)",
            width: "60px",
            height: "2px",
            backgroundColor: theme.primary.main,
          }
        }}
      >
        <Typography
          variant="h4"
          sx={{ 
            fontWeight: "bold", 
            color: theme.primary.dark,
            letterSpacing: "0.5px",
          }}
        >
          Resumen de datos registrados
        </Typography>
        <Typography
          variant="subtitle1"
          sx={{ 
            color: alpha(theme.primary.main, 0.7),
            mt: 1
          }}
        >
          Información de representantes por asociación - Sistema centralizado FCN
        </Typography>
      </Box>

      {/* Botón de actualización de datos */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
        <Button
          variant="outlined"
          onClick={handleRetry}
          startIcon={<RefreshIcon />}
          disabled={isLoading}
          sx={{
            color: theme.primary.main,
            borderColor: theme.primary.main,
            "&:hover": {
              backgroundColor: alpha(theme.primary.main, 0.08),
            },
          }}
        >
          Actualizar datos
        </Button>
      </Box>

      {/* Tarjetas de asociaciones */}
      <Grid container spacing={3}>
        {reporte && reporte.length > 0 ? (
          reporte.map((item, index) => (
            <Grid item xs={12} sm={6} md={6} key={index}>
              <AsociacionResumen 
                item={item} 
                onOpenModal={handleOpenModal} 
              />
            </Grid>
          ))
        ) : (
          <Grid item xs={12}>
            <Paper 
              elevation={2} 
              sx={{ 
                p: 4, 
                textAlign: "center",
                borderRadius: "4px",
                borderTop: `4px solid ${theme.warning.main}`,
              }}
            >
              <AssignmentIcon sx={{ fontSize: 48, color: theme.warning.main, mb: 2 }} />
              <Typography variant="h6" color="textSecondary" gutterBottom>
                No hay datos disponibles para mostrar
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Verifique la conexión con la API o intente actualizar los datos.
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Modal de detalles */}
      <DetallesModal
        open={openModal}
        onClose={handleCloseModal}
        asociacion={selectedAsociacion}
        reporte={reporte}
      />

      {/* Botón para exportar PDF */}
      {reporte && reporte.length > 0 && (
        <Box 
          sx={{ 
            display: "flex", 
            justifyContent: "center", 
            mt: 5,
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              top: "-15px",
              left: "50%",
              transform: "translateX(-50%)",
              width: "30%",
              height: "1px",
              backgroundColor: alpha(theme.primary.main, 0.2),
            }
          }}
        >
          <ExportButton
            onClick={generarPDF}
            startIcon={<PictureAsPdfIcon />}
            disabled={isLoading}
          >
            Exportar Reporte PDF
          </ExportButton>
        </Box>
      )}

      <Box 
        sx={{ 
          mt: 4, 
          pt: 3, 
          borderTop: `1px solid ${alpha(theme.primary.light, 0.2)}`,
          display: 'flex',
          justifyContent: 'center'
        }}
      >
        <Typography variant="body2" color={alpha(theme.primary.main, 0.6)} sx={{ textAlign: 'center' }}>
          © {new Date().getFullYear()} FONDO CAFETERO NACIONAL - Sistema centralizado de gestión
        </Typography>
      </Box>
    </Container>
  );
};

export default Sumatoria;